import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoTimeOutline,
  IoCheckmarkCircle,
  IoArrowForward,
  IoRefresh,
} from "react-icons/io5";

const PendingIVROrdersSection = ({
  API_BASE_URL,
  getAuthToken,
  onCompleteOrder,
  fullPageMode = false,
  providerId = null,
}) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, 30000);
    return () => clearInterval(interval);
  }, [providerId]);

  const fetchPendingOrders = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      let url = `${API_BASE_URL}/pending-ivr/`;
      if (providerId) url += `?provider_id=${providerId}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch pending orders");

      const data = await response.json();

      if (data.success) {
        setPendingOrders(data.data || []);
        setError(null);
      } else {
        setError(data.error || "Failed to load orders");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchPendingOrders(true);

  // ============================================================================
  // LOADING
  // ============================================================================
  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center min-h-[220px]">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="mx-auto mb-4 h-12 w-12 rounded-full border-2 border-slate-200 border-t-teal-600 dark:border-slate-800 dark:border-t-teal-400"
          />
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">
            Loading pending IVR orders...
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR
  // ============================================================================
  if (error && !refreshing) {
    return (
      <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-center shadow-sm dark:border-rose-500/20 dark:bg-rose-500/10">
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-white text-rose-600 shadow-sm dark:bg-slate-900 dark:text-rose-300">
          <span className="text-xl">!</span>
        </div>
        <h3 className="text-base font-extrabold text-slate-900 dark:text-slate-100">
          Couldn’t load IVR orders
        </h3>
        <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-rose-700 active:scale-[0.99]"
        >
          <IoRefresh className="text-lg" />
          Retry
        </button>
      </div>
    );
  }

  // ============================================================================
  // EMPTY
  // ============================================================================
  if (pendingOrders.length === 0 && !loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
          <IoCheckmarkCircle className="text-3xl text-teal-700 dark:text-teal-300" />
        </div>
        <h3 className="text-xl font-extrabold text-slate-900 dark:text-slate-100">
          No pending IVR approvals
        </h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          You’re all caught up. New submissions will appear here automatically.
        </p>
        <button
          onClick={handleRefresh}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
        >
          <IoRefresh className="text-lg" />
          Refresh
        </button>
      </div>
    );
  }

  // ============================================================================
  // STATS
  // ============================================================================
  const approvedCount = pendingOrders.filter((o) => o.can_complete).length;
  const pendingCount = pendingOrders.filter((o) => !o.can_complete).length;

  // ============================================================================
  // WRAPPER
  // ============================================================================
  const Wrapper = fullPageMode ? "div" : motion.div;
  const wrapperProps = fullPageMode
    ? {}
    : {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "mb-6",
      };

  return (
    <Wrapper {...wrapperProps}>
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/50">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 grid h-11 w-11 place-items-center rounded-2xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
              <IoTimeOutline className="text-2xl text-teal-700 dark:text-teal-300" />
            </div>

            <div>
              <h3 className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
                Pending IVR Approvals
              </h3>

              {!fullPageMode ? (
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {approvedCount > 0 && (
                    <span className="font-semibold text-teal-700 dark:text-teal-300">
                      {approvedCount} ready to complete
                    </span>
                  )}
                  {approvedCount > 0 && pendingCount > 0 && (
                    <span className="mx-2 text-slate-300 dark:text-slate-700">
                      •
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="font-semibold text-amber-700 dark:text-amber-300">
                      {pendingCount} awaiting approval
                    </span>
                  )}
                </p>
              ) : (
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Review orders waiting on IVR approval and complete ready ones.
                </p>
              )}
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 shadow-sm hover:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            <IoRefresh className={`text-lg ${refreshing ? "animate-spin" : ""}`} />
            {fullPageMode ? (refreshing ? "Refreshing..." : "Refresh") : null}
          </button>
        </div>

        {/* Full page mode summary */}
        {fullPageMode && (
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Total
              </p>
              <p className="mt-2 text-3xl font-extrabold text-slate-900 dark:text-slate-100">
                {pendingOrders.length}
              </p>
            </div>

            <div className="rounded-2xl border border-teal-100 bg-teal-50 p-4 dark:border-teal-500/20 dark:bg-teal-500/10">
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-700/80 dark:text-teal-300/80">
                Ready to complete
              </p>
              <p className="mt-2 text-3xl font-extrabold text-teal-800 dark:text-teal-200">
                {approvedCount}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80">
                Awaiting approval
              </p>
              <p className="mt-2 text-3xl font-extrabold text-amber-800 dark:text-amber-200">
                {pendingCount}
              </p>
            </div>
          </div>
        )}

        {/* List */}
        <div className="mt-5 space-y-3">
          <AnimatePresence>
            {pendingOrders.map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ delay: index * 0.03 }}
                className={`rounded-2xl border p-5 transition-all ${
                  order.can_complete
                    ? "border-teal-200 bg-teal-50/40 hover:bg-teal-50 dark:border-teal-500/20 dark:bg-teal-500/10 dark:hover:bg-teal-500/15"
                    : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/30 dark:hover:bg-slate-900/50"
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  {/* Left */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-2.5 py-1 text-xs font-extrabold text-slate-900 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-100">
                        #{order.order_number}
                      </span>

                      <span className="text-slate-300 dark:text-slate-700">•</span>

                      <div className="inline-flex min-w-0 items-center gap-2">
                        <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-teal-600 to-emerald-600 text-sm font-extrabold text-white">
                          {order.patient_name?.charAt(0) || "P"}
                        </div>
                        <span className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                          {order.patient_name}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-bold text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
                        {order.care_type === "propacks" ? "ProPacks" : "Advanced Care"}
                      </span>

                      {order.wound_type && (
                        <>
                          <span className="text-slate-300 dark:text-slate-700">•</span>
                          <span className="font-medium">{order.wound_type}</span>
                        </>
                      )}

                      {order.wound_location && (
                        <>
                          <span className="text-slate-300 dark:text-slate-700">•</span>
                          <span className="font-medium">{order.wound_location}</span>
                        </>
                      )}

                      <span className="text-slate-300 dark:text-slate-700">•</span>
                      <span className="font-medium">
                        {new Date(order.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>

                    <div className="mt-3">
                      {order.can_complete ? (
                        <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-extrabold text-teal-800 dark:border-teal-500/20 dark:bg-slate-950/30 dark:text-teal-200">
                          <IoCheckmarkCircle className="text-base" />
                          IVR Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-extrabold text-amber-800 dark:border-amber-500/20 dark:bg-slate-950/30 dark:text-amber-200">
                          <IoTimeOutline className="text-base" />
                          Awaiting approval
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex-shrink-0">
                    {order.can_complete ? (
                      <motion.button
                        onClick={() => onCompleteOrder(order)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-3 text-sm font-extrabold text-white shadow-sm hover:from-teal-500 hover:to-emerald-500 active:scale-[0.99] lg:w-auto"
                        whileHover={{ y: -1 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Complete
                        <IoArrowForward className="text-lg" />
                      </motion.button>
                    ) : (
                      <button
                        disabled
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-5 py-3 text-sm font-extrabold text-slate-500 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-500 lg:w-auto"
                      >
                        <IoTimeOutline className="text-lg" />
                        Waiting
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Footer tip */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-400">
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            Tip:
          </span>{" "}
          Orders marked <span className="font-semibold">IVR Approved</span> are ready. Click{" "}
          <span className="font-semibold">Complete</span> to finish kit selection and submit.
        </div>
      </div>
    </Wrapper>
  );
};

export default PendingIVROrdersSection;
